import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { ClerkProvider, useAuth } from '@clerk/react';
import { API, ApiError, apiRequest } from '../services/api-client';
import type { BillingInterval } from '../../../shared/account-policy';
import { isLocalPreviewHost } from '../config/local-preview-host';
import type { StudioFeatureCapabilities } from '../../../shared/studio-capabilities';

export interface PublicCommerce {
  paddleClientToken?: string | null; paddleEnvironment?: 'sandbox' | 'live' | null;
  countryCode?: string;
  commerceEnabled: boolean; clerkPublishableKey: string | null; termsVersion: string;
  plan: { name: string; amount: number; annualAmount: number; currency: string; entitlementMode: 'unlimited';
    priceId?: { month: string; year: string };
    maxVideoSeconds: number; retentionDays: number; trialsEnabled: boolean; trialDays: number; trialExports: number;
    trialMaxVideoSeconds: number; checkoutEnabled: boolean };
}
export interface Entitlements {
  featureCapabilities?: StudioFeatureCapabilities;
  kind: 'trial' | 'paid' | 'admin' | 'none'; canRender: boolean; canImport: boolean; initialized: boolean; suspended: boolean;
  entitlementMode: 'metered' | 'unlimited'; billingInterval: BillingInterval | null;
  maxVideoSeconds: number; retentionDays: number; remainingExports: number | null; remainingSeconds: number | null;
  startsAt: string | null; endsAt: string | null; trialStartsAt: string | null; trialEndsAt: string | null; signupAt: string | null;
  checkoutEnabled: boolean; trialDays: number; usage: { videos: number; seconds: number };
}
type Status = 'loading' | 'signed-out' | 'ready' | 'error' | 'local';
interface Commerce { config: PublicCommerce | null; entitlement: Entitlements | null; status: Status; error: string; localSignIn: boolean; refresh(): Promise<void> }
const Context = createContext<Commerce | null>(null);
export const useCommerce = () => useContext(Context);

function AccountState({ config, children, localSignIn = false }: { config: PublicCommerce; children: ReactNode; localSignIn?: boolean }) {
  const [entitlement, setEntitlement] = useState<Entitlements | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState('');
  const refresh = useCallback(async () => {
    try { const value = await apiRequest<Entitlements>(`${API}/account/entitlements`); setEntitlement(value); setStatus('ready'); setError(''); }
    catch (cause) {
      setEntitlement(null); setStatus(cause instanceof ApiError && cause.status === 401 ? 'signed-out' : 'error');
      setError(cause instanceof Error ? cause.message : 'Account access is temporarily unavailable.');
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!entitlement?.endsAt) return;
    const delay = Date.parse(entitlement.endsAt) - Date.now() + 25;
    if (delay <= 0) return;
    const timer = setTimeout(() => void refresh(), Math.min(delay, 2_147_483_647));
    return () => clearTimeout(timer);
  }, [entitlement?.endsAt, refresh]);
  return <Context.Provider value={{ config, entitlement, status, error, refresh, localSignIn }}>{children}</Context.Provider>;
}

/** Configuration failures never authorize the editor or private APIs. */
export function CommerceRoot({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PublicCommerce | null>(null);
  const [state, setState] = useState<Status>('loading');
  const [localSignIn, setLocalSignIn] = useState(false);
  const renderMode = location.pathname === '/' && new URLSearchParams(location.search).get('render') === 'video';
  useEffect(() => {
    if (renderMode) return;
    let active = true;
    const load = async () => {
      const local = isLocalPreviewHost(location.hostname);
      const [configuration, capabilities] = await Promise.allSettled([
        apiRequest<PublicCommerce>('/api/public/config'),
        local ? apiRequest<{ localRendering: boolean; localSignIn: boolean }>('/api/local/capabilities') : Promise.resolve(null),
      ]);
      if (!active) return;
      if (configuration.status === 'fulfilled' && configuration.value.commerceEnabled) {
        const value = configuration.value;
        const localAuth = capabilities.status === 'fulfilled' && capabilities.value?.localSignIn === true;
        if (!value.clerkPublishableKey && !localAuth) { setState('error'); return; }
        setLocalSignIn(localAuth); setConfig(value); setState('ready');
      } else if (local && capabilities.status === 'fulfilled' && capabilities.value?.localRendering) setState('local');
      else setState('error');
    };
    void load(); return () => { active = false; };
  }, [renderMode]);
  if (renderMode) return children;
  if (!config) return <Context.Provider value={{ config: null, entitlement: null, status: state, localSignIn: false,
    error: 'Account services are unavailable. Please try again later.', refresh: async () => { if (state !== 'local') location.reload(); } }}>{children}</Context.Provider>;
  if (!config.clerkPublishableKey) return <AccountState config={config} localSignIn={localSignIn}>{children}</AccountState>;
  return <ClerkProvider publishableKey={config.clerkPublishableKey} localization={{ signUp: { continue: { title: 'Create a password' } } }} signInUrl="/sign-in" signUpUrl="/sign-up" signInFallbackRedirectUrl="/account" signUpFallbackRedirectUrl="/account">
    <ManagedAccount config={config}>{children}</ManagedAccount>
  </ClerkProvider>;
}
function ManagedAccount({ config, children }: { config: PublicCommerce; children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <Context.Provider value={{ config, entitlement: null, status: 'loading', error: '', localSignIn: false, refresh: async () => undefined }}>{children}</Context.Provider>;
  return <AccountState key={isSignedIn ? 'signed-in' : 'signed-out'} config={config}>{children}</AccountState>;
}
